/**
 * Growth tab (spec section 59).
 *
 * The power graph is fact: it plots the snapshots we actually stored. The
 * growth figures beside it are inference computed from those snapshots, so they
 * carry their model version, sample count and any warnings the server attached.
 * The growth *flag* ("WHALE", "DORMANT") is the most interpretive thing here and
 * is never shown without its confidence.
 */

import { useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PlayerGrowth } from '@kso/schemas';
import { usePlayerHistory } from '../../../api/queries';
import { Badge, Card, Stat, WarningList } from '../../../components/ui/primitives';
import { EmptyState, QueryBoundary } from '../../../components/ui/states';
import {
  ConfidenceBadge,
  InferenceMark,
  SampleCount,
} from '../../../components/inference/Inference';
import {
  formatCompact,
  formatDate,
  formatDateTime,
  formatDelta,
  formatPercent,
  formatPercentile,
} from '../../../lib/format';
import { growthFlagLabel } from '../../../lib/enumLabels';
import { useT } from '../../../i18n';

const RANGE_DAYS = [7, 30, 90] as const;

const GROWTH_FLAG_TONE: Record<string, string> = {
  WHALE: 'accent',
  FAST_GROWER: 'HIGH',
  RISING: 'HIGH',
  NORMAL: 'neutral',
  DORMANT: 'MEDIUM',
};

export function GrowthTab({
  playerId,
  growth,
}: {
  playerId: string;
  growth: PlayerGrowth | null;
}) {
  const t = useT();
  const [days, setDays] = useState<number>(30);
  const history = usePlayerHistory(playerId, days);

  return (
    <div className="stack">
      <GrowthMetrics growth={growth} />

      <Card
        title={t.growth.historyTitle}
        actions={
          <div className="row" style={{ gap: 4 }}>
            {RANGE_DAYS.map((range) => (
              <button
                key={range}
                type="button"
                className={days === range ? 'btn btn--sm btn--primary' : 'btn btn--sm'}
                onClick={() => setDays(range)}
              >
                {t.common.days(range)}
              </button>
            ))}
          </div>
        }
      >
        <QueryBoundary
          query={history}
          loadingLabel={t.growth.historyLoading}
          isEmpty={(data) => data.points.length === 0}
          empty={
            <EmptyState title={t.growth.historyEmptyTitle} body={t.growth.historyEmptyBody} />
          }
        >
          {(data) => <PowerChart points={data.points} />}
        </QueryBoundary>
      </Card>

      <Card title={t.growth.snapshotLogTitle}>
        <QueryBoundary
          query={history}
          isEmpty={(data) => data.points.length === 0}
          empty={
            <p className="muted small" style={{ margin: 0 }}>
              {t.growth.snapshotLogEmpty}
            </p>
          }
        >
          {(data) => <SnapshotTable points={data.points} />}
        </QueryBoundary>
      </Card>
    </div>
  );
}

function GrowthMetrics({ growth }: { growth: PlayerGrowth | null }) {
  const t = useT();

  if (!growth) {
    return <EmptyState title={t.growth.metricsEmptyTitle} body={t.growth.metricsEmptyBody} />;
  }

  return (
    <Card
      title={
        <span className="row">
          {t.growth.cardTitle} <InferenceMark modelVersion={growth.modelVersion} />
          <SampleCount
            samples={growth.sampleCount}
            noun={t.inference.nouns.snapshots}
            threshold={3}
          />
        </span>
      }
    >
      <div className="grid grid--stats" style={{ marginBottom: 12 }}>
        {/*
          `absentLabel` here says the trend was not computed. It must never read
          as a measured zero: "no history" and "no growth" are different claims
          about this player.
        */}
        <Stat
          label={t.alliance.growth.window24h}
          value={formatDelta(growth.powerGrowth24h)}
          foot={formatPercent(growth.powerGrowthPct24h)}
          absentLabel={t.common.notEnoughHistory}
        />
        <Stat
          label={t.alliance.growth.window7d}
          value={formatDelta(growth.powerGrowth7d)}
          foot={formatPercent(growth.powerGrowthPct7d)}
          absentLabel={t.common.notEnoughHistory}
        />
        <Stat
          label={t.alliance.growth.window30d}
          value={formatDelta(growth.powerGrowth30d)}
          absentLabel={t.common.notEnoughHistory}
        />
        <Stat
          label={t.growth.percentileAlliance}
          value={
            growth.powerPercentileAlliance === null
              ? null
              : formatPercentile(growth.powerPercentileAlliance * 100)
          }
          absentLabel={t.common.notRanked}
        />
        <Stat
          label={t.growth.percentileKingdom}
          value={
            growth.powerPercentileKingdom === null
              ? null
              : formatPercentile(growth.powerPercentileKingdom * 100)
          }
          absentLabel={t.common.notRanked}
        />
      </div>

      <div className="row">
        <span className="muted small">{t.growth.growthFlag}</span>
        {growth.growthFlag ? (
          <Badge
            tone={GROWTH_FLAG_TONE[growth.growthFlag] ?? 'neutral'}
            title={t.growth.growthFlagTitle}
          >
            {growthFlagLabel(t, growth.growthFlag)}
          </Badge>
        ) : (
          <span className="absent">{t.common.notClassified}</span>
        )}
        <ConfidenceBadge
          level={confidenceLevel(growth.confidence)}
          confidence={growth.confidence}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <WarningList warnings={growth.warnings} />
      </div>
    </Card>
  );
}

function confidenceLevel(confidence: number | null): 'HIGH' | 'MEDIUM' | 'LOW' | 'UNKNOWN' {
  if (confidence === null) return 'UNKNOWN';
  if (confidence >= 0.7) return 'HIGH';
  if (confidence >= 0.4) return 'MEDIUM';
  return 'LOW';
}

function PowerChart({
  points,
}: {
  points: Array<{ capturedAt: string; power: number | null; townHallLevel: number | null }>;
}) {
  const t = useT();

  // Points with no recorded power are dropped from the line rather than
  // plotted as zero, which would draw a cliff that never happened.
  const series = points
    .filter((point) => point.power !== null)
    .map((point) => ({
      t: new Date(point.capturedAt).getTime(),
      power: point.power as number,
      tc: point.townHallLevel,
    }))
    .sort((a, b) => a.t - b.t);

  const omitted = points.length - series.length;

  if (series.length < 2) {
    return (
      <EmptyState
        title={t.growth.notEnoughPointsTitle}
        body={t.growth.notEnoughPointsBody(series.length)}
      />
    );
  }

  return (
    <>
      <div style={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis
              dataKey="t"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(value: number) => formatDate(new Date(value).toISOString()) ?? ''}
              stroke="var(--text-faint)"
              fontSize={11}
            />
            <YAxis
              tickFormatter={(value: number) => formatCompact(value) ?? ''}
              stroke="var(--text-faint)"
              fontSize={11}
              width={54}
              domain={['auto', 'auto']}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--bg-raised)',
                border: '1px solid var(--border-strong)',
                borderRadius: 6,
                fontSize: 12,
              }}
              /*
                Recharts draws the crosshair with a hardcoded #ccc unless told
                otherwise -- fine on near-black, nearly invisible on a white
                card, and a colour from outside the scales either way.
              */
              cursor={{ stroke: 'var(--border-strong)' }}
              labelFormatter={(value: number) => formatDateTime(new Date(value).toISOString()) ?? ''}
              formatter={(value: number) => [formatCompact(value) ?? '', t.common.power]}
            />
            <Line
              type="monotone"
              dataKey="power"
              stroke="var(--accent)"
              strokeWidth={2}
              /*
                The fill and the hover ring must be stated, not defaulted:
                recharts fills a dot with #fff and rings the active one in #fff.
                On a white card that paints every real observation as a hole in
                the line -- the chart inverting its own meaning -- so both track
                the surface the Card actually paints.
              */
              dot={{ r: 2, fill: 'var(--bg-raised)', stroke: 'var(--accent)', strokeWidth: 1 }}
              activeDot={{ r: 4, stroke: 'var(--bg-raised)', strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {omitted > 0 ? (
        <p className="faint small" style={{ marginBottom: 0 }}>
          {t.growth.omittedPoints(omitted)}
        </p>
      ) : null}
    </>
  );
}

function SnapshotTable({
  points,
}: {
  points: Array<{
    capturedAt: string;
    power: number | null;
    townHallLevel: number | null;
    kills: number | null;
    allianceId: string | null;
  }>;
}) {
  const t = useT();
  const ordered = [...points].sort(
    (a, b) => new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime(),
  );

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            <th>{t.growth.columns.captured}</th>
            <th className="num">{t.common.power}</th>
            <th className="num">{t.common.tc}</th>
            <th className="num">{t.common.kills}</th>
            <th>{t.common.alliance}</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((point) => (
            <tr key={point.capturedAt}>
              <td className="nowrap">{formatDateTime(point.capturedAt)}</td>
              <td className="num">
                {point.power === null ? (
                  <span className="absent">{t.common.notRecorded}</span>
                ) : (
                  formatCompact(point.power)
                )}
              </td>
              <td className="num">
                {point.townHallLevel === null ? (
                  <span className="absent">{t.common.dash}</span>
                ) : (
                  point.townHallLevel
                )}
              </td>
              <td className="num">
                {point.kills === null ? (
                  <span className="absent">{t.common.notRecorded}</span>
                ) : (
                  formatCompact(point.kills)
                )}
              </td>
              <td className="mono">
                {point.allianceId === null ? (
                  <span className="absent">{t.common.none}</span>
                ) : (
                  point.allianceId
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
